
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect } from "react";
import { X, CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parseISO, startOfDay } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";

interface Project {
  id?: string;
  name: string;
  description: string;
  camera_budget: number;
  ge_budget: number;
  budget_enabled: boolean;
  prep_date?: string | null;
  pre_light_date?: string | null;
  shoot_dates?: string[];
  camera_enabled?: boolean;
  ge_enabled?: boolean;
}

interface ProjectFormProps {
  onClose: () => void;
  onSubmit: (project: Project) => void;
  initialData?: Project;
}

export default function ProjectForm({ onClose, onSubmit, initialData }: ProjectFormProps) {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [cameraBudget, setCameraBudget] = useState(initialData?.camera_budget?.toString() || "0");
  const [geBudget, setGeBudget] = useState(initialData?.ge_budget?.toString() || "0");
  const [budgetEnabled, setBudgetEnabled] = useState(initialData?.budget_enabled ?? true);
  const [cameraEnabled, setCameraEnabled] = useState(initialData?.camera_enabled ?? true);
  const [geEnabled, setGeEnabled] = useState(initialData?.ge_enabled ?? true);
  const [prepDate, setPrepDate] = useState<Date | undefined>(
    initialData?.prep_date ? startOfDay(parseISO(initialData.prep_date)) : undefined
  );
  const [preLightDate, setPreLightDate] = useState<Date | undefined>(
    initialData?.pre_light_date ? startOfDay(parseISO(initialData.pre_light_date)) : undefined
  );
  const [shootDates, setShootDates] = useState<Date[]>(
    initialData?.shoot_dates?.map(date => startOfDay(parseISO(date))) || []
  );
  
  const isMobile = useIsMobile();

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description || "");
      setCameraBudget(initialData.camera_budget?.toString() || "0");
      setGeBudget(initialData.ge_budget?.toString() || "0");
      setBudgetEnabled(initialData.budget_enabled ?? true);
      setCameraEnabled(initialData.camera_enabled ?? true);
      setGeEnabled(initialData.ge_enabled ?? true);
      setPrepDate(initialData.prep_date ? startOfDay(parseISO(initialData.prep_date)) : undefined);
      setPreLightDate(initialData.pre_light_date ? startOfDay(parseISO(initialData.pre_light_date)) : undefined);
      setShootDates(initialData.shoot_dates?.map(date => startOfDay(parseISO(date))) || []);
    }
  }, [initialData]);

  const formatDateToString = (date: Date | undefined): string | null => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const formattedShootDates = shootDates
      .map(date => formatDateToString(date))
      .filter(Boolean) as string[];

    const updatedProject: Project = {
      ...(initialData?.id ? { id: initialData.id } : {}),
      name,
      description,
      camera_budget: budgetEnabled && cameraEnabled ? Number(cameraBudget) || 0 : 0,
      ge_budget: budgetEnabled && geEnabled ? Number(geBudget) || 0 : 0,
      budget_enabled: budgetEnabled,
      camera_enabled: cameraEnabled,
      ge_enabled: geEnabled,
      prep_date: formatDateToString(prepDate),
      pre_light_date: formatDateToString(preLightDate),
      shoot_dates: formattedShootDates,
    };

    onSubmit(updatedProject);
  };

  const handleShootDateSelect = (dates: Date[] | undefined) => {
    if (!dates) return;
    setShootDates(dates.map(date => startOfDay(date)));
  };

  const formatSelectedDates = (dates: Date[]) => {
    if (dates.length === 0) return "Select shoot dates";
    
    // For mobile, truncate the list if there are many dates
    if (isMobile && dates.length > 2) {
      return `${dates.length} dates selected`;
    }
    
    return dates.map(date => format(date, "MMM d, yyyy")).join(", ");
  };

  return (
    <Card className="w-full max-w-lg bg-background/40 backdrop-blur-lg border border-white/5 shadow-lg overflow-auto max-h-[90vh]">
      <CardHeader className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b border-white/5 flex flex-row items-center justify-between p-4">
        <CardTitle className="text-xl font-bold">{initialData ? "Edit Project" : "Create New Project"}</CardTitle>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-base">Project Name</Label>
            <Input
              id="name"
              placeholder="Enter project name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="bg-background/30 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description" className="text-base">Description</Label>
            <Textarea
              id="description"
              placeholder="Enter project description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-background/30 border-white/10 min-h-[80px]"
            />
          </div>
          
          <div className="flex items-center space-x-2 py-2">
            <Switch
              id="budget-enabled"
              checked={budgetEnabled}
              onCheckedChange={setBudgetEnabled}
              className="data-[state=checked]:bg-primary"
            />
            <Label htmlFor="budget-enabled" className="text-base font-medium">Enable Budget Tools</Label>
          </div>
          
          <div className="flex items-center space-x-2 py-2">
            <Switch
              id="camera-enabled"
              checked={cameraEnabled}
              onCheckedChange={setCameraEnabled}
              className="data-[state=checked]:bg-primary"
            />
            <Label htmlFor="camera-enabled" className="text-base font-medium">Camera Department</Label>
          </div>
          
          <div className="flex items-center space-x-2 py-2">
            <Switch
              id="ge-enabled"
              checked={geEnabled}
              onCheckedChange={setGeEnabled}
              className="data-[state=checked]:bg-primary"
            />
            <Label htmlFor="ge-enabled" className="text-base font-medium">G&E Department</Label>
          </div>
          
          {budgetEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {cameraEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="cameraBudget" className="text-base">Camera Department Budget</Label>
                  <Input
                    id="cameraBudget"
                    type="number"
                    placeholder="0"
                    value={cameraBudget}
                    onChange={(e) => setCameraBudget(e.target.value)}
                    className="bg-background/30 border-white/10"
                  />
                </div>
              )}
              {geEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="geBudget" className="text-base">G&E Department Budget</Label>
                  <Input
                    id="geBudget"
                    type="number"
                    placeholder="0"
                    value={geBudget}
                    onChange={(e) => setGeBudget(e.target.value)}
                    className="bg-background/30 border-white/10"
                  />
                </div>
              )}
            </div>
          )}
          
          <div className="space-y-2">
            <Label className="text-base">Prep Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal bg-background/30 border-white/10",
                    !prepDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {prepDate ? format(prepDate, "MMM d, yyyy") : "Select prep date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={prepDate}
                  onSelect={setPrepDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label className="text-base">Pre-Light Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal bg-background/30 border-white/10",
                    !preLightDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {preLightDate ? format(preLightDate, "MMM d, yyyy") : "Select pre-light date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={preLightDate}
                  onSelect={setPreLightDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label className="text-base">Shoot Dates</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full min-h-[2.5rem] h-auto justify-start text-left font-normal whitespace-normal bg-background/30 border-white/10",
                    shootDates.length === 0 && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                  <span className="line-clamp-2">{formatSelectedDates(shootDates)}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="multiple"
                  selected={shootDates}
                  onSelect={handleShootDateSelect}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
        <CardFooter className="justify-end space-x-2 border-t border-white/5 p-4 sticky bottom-0 bg-background/80 backdrop-blur-lg">
          <Button variant="outline" type="button" onClick={onClose} className="border-white/10 hover:bg-white/5">
            Cancel
          </Button>
          <Button type="submit" className="bg-primary hover:bg-primary/90">
            {initialData ? "Save Changes" : "Create Project"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
